<template>
  <div v-if="isPrescriberLoading">
    <div class="tw-text-center tw-text-gray-500 tw-py-6">Loading Pharmacy Information...</div>
  </div>
  <div v-else>
    <div>
      <h2
        class="tw-text-xl tw-text-primary-500 tw-bg-transparent tw-shadow-none tw-font-sans tw-inline-flex tw-items-center border-0 focus:tw-outline-none focus:tw-text-primary-700 tw-mb-4"
      >
        Pharmacy
      </h2>

      <div>
        <base-select
          label="Pharmacy Location"
          v-model="selectedPharmacyId"
          :options="pharmacyOptions"
          placeholder="Select a Pharmacy for Submission"
          class="tw-mb-4"
          :error="!isPharmacySelected && triedToSubmit"
          :error-message="!isPharmacySelected && triedToSubmit ? 'Please select a pharmacy.' : ''"
          label-key="text"
          value-key="value"
        />

        <!-- <base-select
          v-if="!isPrescriberLoading"
          label="Prescriber"
          v-model="selectedPrescriberId"
          :options="prescriberOptions"
          placeholder="Select a Prescriber"
          class="tw-mb-4"
          :error="!isPrescriberSelected && triedToSubmit"
          :error-message="!isPrescriberSelected && triedToSubmit ? 'Please select a prescriber.' : ''"
          label-key="text"
          value-key="value"
        />
        <base-skeleton-loader v-else class="tw-h-10 tw-w-1/2 tw-mb-3" /> -->
        <div class="tw-flex tw-mb-4">
          <div class="tw-text-gray-800 tw-font-normal tw-text-sm">Supervising Prescriber:</div>
          <span :class="['tw-ml-2 tw-my-auto tw-text-sm', !supervisingPrescriberName ? 'tw-text-red-500' : 'tw-text-gray-800']">
            {{ supervisingPrescriberName || "Not Assigned" }}
          </span>
        </div>

        <div class="tw-flex md:tw-flex-row md:tw-space-x-4 tw-mb-6">
          <div class="tw-flex tw-items-center tw-mb-4 md:tw-mb-0 tw-shrink-0">
            <base-toggle v-model="expediteShipment" class="tw-mr-2" />
            <span class="tw-text-gray-700 tw-text-sm">Expedite Shipment?</span>
          </div>

          <div class="tw-grow tw-w-full">
            <base-text-field label="Comment" placeholder="e.g., Patient will be leaving town in 1 week, please expedite" v-model="comment" :maxLength="effectiveMaxCommentLength" />
            <div class="tw-text-xs tw-text-gray-500 tw-mt-1">{{ comment.length }} / {{ effectiveMaxCommentLength }}</div>
          </div>
        </div>
        <inline-error id="business-order-error" :title="errorTitle" :message="errorMessage" :error="error" class="tw-mb-4" />
        <div v-if="showWarning" class="tw-text-red-500 tw-mb-2 tw-text-sm">
          Prescription must be accepted and plan must be active before submitting to pharmacy.
        </div>
        <div v-if="needsDetailsCheck" class="tw-text-red-500 tw-mb-2 tw-text-sm">
          Please check activity details before submitting to pharmacy.
        </div>
        <!-- NPI Warning -->
        <div v-if="isPrescriberNpiMissing" class="tw-text-red-500 tw-mb-2 tw-text-sm">
          NPI is not set for the selected Supervising Prescriber.
        </div>
        <div class="tw-flex tw-justify-between tw-items-center tw-border-t tw-border-gray-200 tw-pt-6">
          <base-button type="secondary" @click="backToProfile">Back to Profile</base-button>
          <div class="tw-flex tw-space-x-4">
            <base-button type="secondary" @click="createClarificationWithData" :disabled="isLoading">Create Clarification</base-button>
            <div
              :title="isSubmitDisabled ? 'This order has already been submitted.' : showWarning ? 'Prescription must be accepted and plan must be active before submitting' : ''"
            >
              <base-button
                type="primary"
                @click="onSubmitOrder"
                :disabled="!isPharmacySelected || isLoading || !canSubmit || isSubmitDisabled || needsDetailsCheck || !supervisingPrescriberName || isPrescriberNpiMissing"
              >
                Submit to Pharmacy
              </base-button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import InlineError from "@/components/InlineError";

export default {
  name: "PharmacySelection",
  components: {
    InlineError,
  },
  props: {
    pharmacyData: {
      type: Object,
      default: null,
      required: false,
    },
    activityData: {
      type: Object,
      default: null,
      required: false,
    },
    prescriptionData: {
      type: Object,
      default: null,
      required: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
      required: false,
    },
    errorTitle: {
      type: String,
      default: "",
    },
    errorMessage: {
      type: String,
      default: "",
    },
    error: {
      type: [String, Object],
      default: null,
    },
    isSubmitDisabled: {
      type: Boolean,
      default: false,
    },
    prescriberOptions: {
      type: Array,
      default: () => [],
    },
    defaultPrescriberId: {
      type: [String, Number],
      default: null,
    },
    isPrescriberLoading: {
      type: Boolean,
      default: false,
    },
    needsDetailsCheck: {
      type: Boolean,
      default: false,
    },
    supervisingPrescriberName: {
      type: String,
      default: "",
    },
    maxCommentLength: {
      type: Number,
      default: 120, // fallback to 120 if not passed from parent
    },
    isPrescriberNpiMissing: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  data() {
    return {
      selectedPharmacyId: null,
      expediteShipment: false,
      comment: "",
      triedToSubmit: false,
      selectedPrescriberId: null,
      lifefileBasedPharmacies: ["Innovation", "Lifefile", "OmniScript"],
    };
  },
  computed: {
    pharmacyOptions() {
      // pharmacyData and availablePharmacies exist and is an array
      if (!this.pharmacyData || !Array.isArray(this.pharmacyData.availablePharmacies)) return [];

      return this.pharmacyData.availablePharmacies.map((pharmacy) => ({
        value: pharmacy.value,
        text: pharmacy.name,
      }));
    },

    isPharmacySelected() {
      return this.selectedPharmacyId !== null && this.selectedPharmacyId !== "";
    },
    canSubmit() {
      if (!this.activityData || !this.prescriptionData) return false;

      // Check if prescription is accepted and userBusinessPlan status is 'Active'
      const isAccepted = this.prescriptionData.status && this.prescriptionData.status.isAccepted === true;
      const isPlanActive = this.activityData.userBusinessPlan && this.activityData.userBusinessPlan.status === "Active";

      return isAccepted && isPlanActive;
    },

    showWarning() {
      if (!this.activityData || !this.prescriptionData) return false;

      const isAccepted = this.prescriptionData.status && this.prescriptionData.status.isAccepted === true;
      const isPlanActive = this.activityData.userBusinessPlan && this.activityData.userBusinessPlan.status === "Active";
      return !isAccepted || !isPlanActive;
    },
    isPrescriberSelected() {
      return this.selectedPrescriberId !== null && this.selectedPrescriberId !== "";
    },
    effectiveMaxCommentLength() {
      const expediteText = this.expediteShipment && this.lifefileBasedPharmacies.includes(this.selectedPharmacyId) ? "This is expedite shipment." : "";

      // Reserve space for expedite text + one space if applicable
      const reservedLength = expediteText ? expediteText.length + 1 : 0;
      const parentMax = !this.lifefileBasedPharmacies.includes(this.selectedPharmacyId) ? 500 - (120 - this.maxCommentLength) : this.maxCommentLength || 120;
      const effectiveMax = parentMax - reservedLength;
      return effectiveMax > 0 ? effectiveMax : 0;
    },
  },
  watch: {
    "pharmacyData.availablePharmacies": {
      immediate: true,
      handler(newOptions) {
        if (newOptions && newOptions.length > 0 && this.selectedPharmacyId === null) {
          this.selectedPharmacyId = newOptions[0].value;
        }
      },
    },
    defaultPrescriberId: {
      immediate: true,
      handler(newVal) {
        if (newVal) this.selectedPrescriberId = newVal;
      },
    },
    selectedPharmacyId() {
      if (this.comment.length > this.effectiveMaxCommentLength) {
        this.comment = this.comment.substring(0, this.effectiveMaxCommentLength);
      }
    },
    expediteShipment(newVal) {
      if (newVal == true && this.comment.length > this.effectiveMaxCommentLength) {
        this.comment = this.comment.substring(0, this.effectiveMaxCommentLength);
      }
    },
    comment(newVal) {
      if (newVal.length > this.effectiveMaxCommentLength) {
        this.comment = newVal.substring(0, this.effectiveMaxCommentLength);
      }
    },
  },
  methods: {
    onSubmitOrder() {
      this.triedToSubmit = true;
      if (this.isPharmacySelected) {
        this.$emit("submit-order", {
          selectedPharmacyId: this.selectedPharmacyId,
          expediteShipment: this.expediteShipment,
          selectedPrescriberId: this.selectedPrescriberId,
          comment: this.comment.trim(),
        });
      } else {
        this.EventBus.$emit("GLOBAL_NOTIFY", {
          title: "Validation Error",
          message: "Please select a pharmacy for submission.",
          type: "warning",
        });
      }
    },
    createClarificationWithData() {
      if (this.activityData) {
        this.$emit("create-clarification");
      } else {
        // Handle case where activityData is not available
        this.EventBus.$emit("GLOBAL_NOTIFY", {
          title: "Error",
          message: "Cannot create clarification: Activity data not available.",
          type: "error",
        });
      }
    },
    backToProfile() {
      this.$emit("back-to-profile");
    },
  },
};
</script>
